import { useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from '@grandvisionhq/common';
import { usePlacePredictions } from '@grandvisionhq/locations';
import { Hooks } from '@grandvisionhq/stores-v2';
import { useDebouncedEffect } from '@grandvisionhq/utils';

/**
 * In order to retrieve a store closest to a given address, we need to execute a few steps:
 * 1. Get a Google Maps Place ID from the query
 * 2. Get detailed information from this ID that includes latitudes and longitudes
 * 3. Get the nearest store from the API based on the latitudes and longitudes
 * 4. Return the global store id of the nearest store
 */ const useNearestStore = (query, isEnabled)=>{
    const [loading, setLoading] = useState(false);
    const [getPlacePredictions, placePredictions] = usePlacePredictions({
        variables: {
            name: query
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache'
    });
    const [getPlaceById, placeById] = Hooks.usePlaceById({
        id: ''
    });
    const [getStoresByDistance, storesByDistance] = Hooks.useStoresByDistanceWithoutInventory({
        searchGeoLocation: {
            lat: '',
            lon: ''
        }
    });
    const handleOnSearch = useCallback(()=>{
        if (query.length && isEnabled) {
            setLoading(true);
            getPlacePredictions({
                variables: {
                    name: query
                }
            });
        }
    }, [
        query,
        isEnabled,
        getPlacePredictions
    ]);
    useDebouncedEffect(()=>{
        handleOnSearch();
    }, 500, [
        query
    ]);
    useEffect(()=>{
        if (!placeById.data) {
            setLoading(false);
            return;
        }
        const { coordinates: { lat , lon  } ,  } = placeById.data;
        getStoresByDistance({
            variables: {
                // TODO: Check if need to filter based on ServicesNotAvailable enum?
                maxResults: 1,
                searchGeoLocation: {
                    lat,
                    lon
                }
            }
        });
    }, [
        placeById.data,
        getStoresByDistance
    ]);
    useEffect(()=>{
        const predictions = placePredictions.data;
        if (!predictions || !predictions.length || !predictions[0]) {
            setLoading(false);
            return;
        }
        getPlaceById({
            variables: {
                id: predictions[0].id
            }
        });
    }, [
        placePredictions.data,
        getPlaceById
    ]);
    const storeId = useMemo(()=>{
        const stores = storesByDistance.data;
        setLoading(false);
        if (!stores || !stores.length || !stores[0]) {
            return;
        }
        return stores[0].globalStoreId;
    }, [
        storesByDistance.data
    ]);
    useEffect(()=>{
        const error = placePredictions.error || placeById.error || storesByDistance.error;
        if (error) {
            setLoading(false);
            console.error(error.message);
            toast.warning('checkout.errors.serviceStoreNotFound');
        }
    }, [
        placePredictions.error,
        placeById.error,
        storesByDistance.error
    ]);
    return [
        storeId,
        loading
    ];
};

export { useNearestStore };
//# sourceMappingURL=use-nearest-store.js.map
